import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IQuotationDetail } from '../../../models/quotations.models';
import { IOrderUser } from 'src/app/features/orders/models/order.model';
import { userInitials } from 'src/app/shared/utils/order-users-dedupe';
import { getUserColor } from 'src/app/shared/utils/user-color.util';
import { ITableColumnSetting } from 'src/app/shared/table-preferences.service';
import {
  IQuickActionField,
  QuickActionFieldKey,
  QuickActionsPreferencesService,
} from 'src/app/shared/quick-actions-preferences.service';

/**
 * The action a chip requests when clicked. Handled by the host panel.
 *
 * `missing` tells the host whether the chip was showing the amber "Missing" tag,
 * so a resolved value can simply reveal the Details tab while an unresolved one
 * jumps straight into the editor that fixes it.
 */
export type QuickActionEvent =
  | { type: 'edit-company'; missing: boolean }
  | { type: 'edit-location'; missing: boolean }
  /** Reveal the Details tab and focus the matching input. */
  | { type: 'focus-field'; field: 'po_number' | 'quote_id' }
  | { type: 'copy'; value: string };

/** A chip resolved against the current record, ready to render. */
interface IQuickActionChip {
  key: QuickActionFieldKey;
  label: string;
  /** Rendered value; chips with no value are dropped entirely. */
  value: string;
  /** Leading icon name from `assets/icones`. */
  icon: string;
  /** Interactive chips get the hover highlight and a pointer. */
  interactive: boolean;
  /** Chips whose action is a clipboard copy show the trailing copy affordance. */
  copyable: boolean;
  /** Chips that open an editor show the trailing chevron. */
  expandable: boolean;
  /**
   * Unresolved value. The chip then renders the bare "Missing" tag — no field
   * label prefix — and clicking it opens the relevant editor directly.
   */
  missing: boolean;
  /** Assignee renders an initials avatar instead of a leading icon. */
  avatar?: { initials: string; color: string };
  /** Opens the inline date picker rather than emitting an action. */
  datePicker?: boolean;
  /** Optional tooltip (the created chip shows the full timestamp). */
  tooltip?: string;
  /**
   * Second tooltip line, rendered smaller. Used by the location chip to show the
   * shipping address under the location name.
   */
  tooltipDetail?: string;
  /** Long values are width-capped and ellipsised; these get a tooltip. */
  truncate?: boolean;
}

/**
 * The quick-actions row (WB-7478): a strip under the panel header that surfaces
 * up to five context fields and makes each actionable in place.
 *
 * Presentational — it renders chips and emits intent. The host panel owns the
 * editors, because company/location edits go through the panel's details draft
 * and only persist on save; an inline popover here would imply an immediate
 * commit that does not happen. Assignee is the exception: its popover writes
 * straight through, so it lives in this template.
 */
@Component({
  selector: 'app-order-quick-actions-row',
  templateUrl: './order-quick-actions-row.component.html',
  styleUrls: ['./order-quick-actions-row.component.scss'],
})
export class OrderQuickActionsRowComponent {
  @Input() set quotation(value: IQuotationDetail | null | undefined) {
    this.record = value ?? null;
    this.rebuild();
  }

  @Input() set fields(value: IQuickActionField[]) {
    this.fieldList = (value ?? []).map((field) => ({ ...field }));
    this.rebuild();
  }

  /** When the record itself is read-only, values still show but actions are off. */
  @Input() set readonly(value: boolean) {
    this.isReadonly = !!value;
    this.rebuild();
  }

  /**
   * Pending values from the host's details draft. The chips render these in
   * preference to the persisted record so an edit — here or in the Details tab —
   * shows on the row immediately, rather than only once the order is saved.
   *
   * Nullish means "host supplied nothing, fall back to the record"; an empty
   * string is a real value (the user cleared the field) and renders as `--`.
   */
  @Input() set shippingDate(value: string | undefined | null) {
    this.draftShippingDate = value ?? null;
    this.rebuild();
  }

  @Input() set poNumber(value: string | undefined | null) {
    this.draftPoNumber = value ?? null;
    this.rebuild();
  }

  @Input() set quoteId(value: string | undefined | null) {
    this.draftQuoteId = value ?? null;
    this.rebuild();
  }

  @Input() set companyName(value: string | undefined | null) {
    this.draftCompanyName = value ?? null;
    this.rebuild();
  }

  @Input() set locationName(value: string | undefined | null) {
    this.draftLocationName = value ?? null;
    this.rebuild();
  }

  @Input() set locationAddress(value: string | undefined | null) {
    this.draftLocationAddress = value ?? null;
    this.rebuild();
  }

  @Output() action = new EventEmitter<QuickActionEvent>();
  /** Dismisses the whole row from the X next to the field picker. */
  @Output() hide = new EventEmitter<void>();
  /** Emits the date chosen in the inline picker (null clears it). */
  @Output() shippingDateChange = new EventEmitter<Date | null>();
  @Output() fieldsChange = new EventEmitter<IQuickActionField[]>();
  /** Bubbles the reassignment so the host can refresh the record. */
  @Output() ownerChanged = new EventEmitter<IOrderUser>();

  readonly maxVisible = QuickActionsPreferencesService.MAX_VISIBLE;

  chips: IQuickActionChip[] = [];
  fieldList: IQuickActionField[] = [];
  /** The field list adapted for `ui-table-column-settings`. */
  pickerColumns: ITableColumnSetting[] = [];

  private record: IQuotationDetail | null = null;
  private isReadonly = false;
  private draftShippingDate: string | null = null;
  private draftPoNumber: string | null = null;
  private draftQuoteId: string | null = null;
  private draftCompanyName: string | null = null;
  private draftLocationName: string | null = null;
  private draftLocationAddress: string | null = null;

  /** Active hover tooltip, positioned from the hovered chip's rect. */
  tip: { title: string; detail?: string; top: string; left: string } | null = null;

  /**
   * The order handed to `app-order-owner-popover` for the assignee chip. A cached
   * field, not a getter: bound in the template, a getter returned a new object
   * literal on every change-detection pass and reassigned the popover's input.
   */
  ownerTarget: { id: number; owner?: IOrderUser | null } = { id: 0, owner: null };

  showTip(chip: IQuickActionChip, event: Event): void {
    if (!chip.tooltip && !chip.tooltipDetail) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tip = {
      title: chip.tooltip ?? chip.value,
      detail: chip.tooltipDetail,
      top: `${rect.bottom + 6}px`,
      left: `${rect.left}px`,
    };
  }

  hideTip(): void {
    this.tip = null;
  }

  onChipClick(chip: IQuickActionChip): void {
    if (!chip.interactive) return;
    switch (chip.key) {
      case 'company':
        this.action.emit({ type: 'edit-company', missing: chip.missing });
        return;
      case 'location':
        this.action.emit({ type: 'edit-location', missing: chip.missing });
        return;
      case 'assignee':
        // Handled by the owner popover's own menu trigger, not an emit.
        return;
      case 'shipping_date':
        // Handled by the inline date picker's own trigger, not an emit.
        return;
      case 'po_number':
      case 'quote_id':
        // These are free-text fields owned by the details draft, so the row sends
        // the user to that input rather than editing in place.
        this.action.emit({ type: 'focus-field', field: chip.key });
        return;
      default:
        this.action.emit({ type: 'copy', value: chip.value });
    }
  }

  /** Current shipping date, for seeding the inline picker. */
  get shippingDateValue(): Date | null {
    const raw = this.effectiveShippingDate;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /** Draft value when the host supplied one, else whatever is persisted. */
  private get effectiveShippingDate(): string | null {
    return this.draftShippingDate ?? this.record?.shipping_date ?? null;
  }

  private get effectivePoNumber(): string | null {
    return this.draftPoNumber ?? this.record?.customer_po_number ?? null;
  }

  private get effectiveQuoteId(): string | null {
    return this.draftQuoteId ?? this.record?.quote_id ?? null;
  }

  private get effectiveCompanyName(): string | null {
    return this.draftCompanyName ?? this.record?.order_company?.name ?? null;
  }

  private get effectiveLocationName(): string | null {
    return this.draftLocationName ?? this.record?.location?.name ?? null;
  }

  private get effectiveLocationAddress(): string | null {
    return this.draftLocationAddress ?? this.record?.location?.shipping_address ?? null;
  }

  /**
   * The shared column-settings popover emits the full ordered list. Clamp it to
   * `MAX_VISIBLE` here — that component has no cap of its own, and the row must
   * never wrap or scroll horizontally.
   */
  onPickerChange(columns: ITableColumnSetting[]): void {
    let shown = 0;
    const fields: IQuickActionField[] = columns.map((column) => {
      let visible = !!column.visible;
      if (visible) {
        shown += 1;
        if (shown > this.maxVisible) visible = false;
      }
      return {
        key: column.key as QuickActionFieldKey,
        label: column.label,
        visible,
      };
    });

    this.fieldList = fields;
    this.rebuild();
    this.fieldsChange.emit(fields);
  }

  /**
   * Resolve the visible fields against the record. Fields the record does not
   * have are dropped rather than rendered as an empty placeholder, per the spec —
   * except company and location, which surface a "Missing" tag instead, since the
   * row doubles as the place unresolved AI extraction gets fixed.
   */
  private rebuild(): void {
    this.ownerTarget = { id: this.record?.id ?? 0, owner: this.record?.owner ?? null };

    this.chips = this.fieldList
      .filter((field) => field.visible)
      .map((field) => this.buildChip(field))
      .filter((chip): chip is IQuickActionChip => !!chip);

    // Hidden fields are appended so the picker can offer them under "hidden",
    // matching how the table column pickers behave.
    this.pickerColumns = this.fieldList.map((field) => ({
      key: field.key,
      label: field.label,
      visible: field.visible,
    }));
  }

  private buildChip(field: IQuickActionField): IQuickActionChip | null {
    const record = this.record;
    if (!record) return null;
    const editable = !this.isReadonly;

    switch (field.key) {
      case 'company': {
        const name = this.effectiveCompanyName?.trim();
        if (!name || name === 'N/A') {
          return this.chip(field, '', 'company_icon', {
            interactive: editable,
            expandable: editable,
            missing: true,
          });
        }
        return this.chip(field, name, 'company_icon', {
          interactive: editable,
          expandable: editable,
          truncate: true,
          tooltip: name,
        });
      }
      case 'location': {
        const name = this.effectiveLocationName?.trim();
        if (!name || name === 'N/A') {
          return this.chip(field, '', 'location', {
            interactive: editable,
            expandable: editable,
            missing: true,
          });
        }
        return this.chip(field, name, 'location', {
          interactive: editable,
          expandable: editable,
          truncate: true,
          tooltip: name,
          // Second, smaller tooltip line: the address disambiguates two sites
          // that share a name.
          tooltipDetail: this.effectiveLocationAddress?.trim() || undefined,
        });
      }
      case 'assignee': {
        const name = record.owner?.name?.trim();
        if (!name) return null;
        return this.chip(field, name, '', {
          interactive: editable,
          expandable: editable,
          avatar: {
            initials: userInitials(name),
            color: getUserColor(record.owner as never),
          },
        });
      }
      // PO and Quote ID stay on the row even when unset — an empty value is
      // exactly when the user needs the shortcut to go and fill it in — so they
      // render a `--` placeholder instead of dropping out.
      case 'po_number':
        return this.chip(field, this.effectivePoNumber || '--', 'tag', {
          interactive: editable,
          expandable: editable,
          truncate: true,
        });
      case 'quote_id':
        return this.chip(field, this.effectiveQuoteId || '--', 'tag', {
          interactive: editable,
          expandable: editable,
          truncate: true,
        });
      case 'shipping_date':
        return this.chip(field, this.formatDate(this.effectiveShippingDate) || '--', 'calendar', {
          interactive: editable,
          expandable: editable,
          datePicker: editable,
        });
      case 'order_id': {
        const value = record.name;
        if (!value) return null;
        return this.chip(field, value, 'tag', { interactive: true, copyable: true });
      }
      case 'created': {
        const value = record.date_order;
        if (!value) return null;
        // Read-only by design: a label, not a control.
        return this.chip(field, value, 'calendar', { interactive: false, tooltip: value });
      }
      default:
        return null;
    }
  }

  /** `dd/MM/yy`, matching the date format the panel's own inputs use. */
  private formatDate(raw: string | undefined | null): string {
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${`${date.getFullYear()}`.slice(-2)}`;
  }

  private chip(
    field: IQuickActionField,
    value: string,
    icon: string,
    options: {
      interactive: boolean;
      copyable?: boolean;
      expandable?: boolean;
      missing?: boolean;
      datePicker?: boolean;
      avatar?: { initials: string; color: string };
      tooltip?: string;
      tooltipDetail?: string;
      truncate?: boolean;
    }
  ): IQuickActionChip {
    return {
      key: field.key,
      label: field.label,
      value,
      icon,
      interactive: options.interactive,
      copyable: !!options.copyable,
      expandable: !!options.expandable,
      missing: !!options.missing,
      datePicker: !!options.datePicker,
      avatar: options.avatar,
      tooltip: options.tooltip,
      tooltipDetail: options.tooltipDetail,
      truncate: !!options.truncate,
    };
  }
}
